import { useState } from 'react';

// #3165 — sentinel <option> value for "Other…" in a directoryOptions select.
// Never a real owner name (project member / stakeholder names are
// validated non-empty, human-entered text; this string is reserved).
const DIRECTORY_OTHER_VALUE = '__disposition_card_other__';

// #2544 — the generic "agent-proposed item" DISPOSE card (Part 1 of the
// shared core): proposal text + labelled option buttons + optional
// edit-then-accept, parameterized by (option-set, accept-handler).
// Dimension-agnostic — built fresh from the Open Questions dispose UI
// (OpenQuestionsPage.tsx) as the reference shape, WITHOUT touching that
// page: extracting it in place would have meant refactoring ~250 lines of
// tightly-coupled per-row OQ state (actingId/deferringId/resolvingId/
// escalatingId, each row-scoped) under this story's deadline pressure, with
// real risk of an OQ regression for no gain THIS story needs. Adopted for
// Risk now (RisksPage.tsx); OQ can adopt it later as its own, separately
// reviewed refactor — no OQ regression either way, since OQ's page is
// unchanged by this component's existence.
//
// A "disposition" here is: pick ONE labelled option, fill in whatever text
// field(s) that option requires (0, 1, or 2 — e.g. Risk's Carried-forward
// needs both a phase AND an owner), then Accept in one action. An option
// with zero fields (e.g. a bare "Escalate" with no free text) accepts
// immediately on click, no textarea shown.

export interface DispositionField {
  key: string;
  label: string;
  placeholder?: string;
  /** Renders a single-line <input> instead of a <textarea> — for short
   *  structured values (a phase name, an owner) rather than prose. */
  short?: boolean;
  /** Renders a <select> populated from these options instead of a
   *  free-text input/textarea — for a value constrained to a fixed
   *  vocabulary (e.g. #2611's canonical carry-forward phase). Takes
   *  precedence over `short` and `directoryOptions` when set. */
  options?: { value: string; label: string }[];
  /**
   * #3165 — renders a <select> populated from these suggestions PLUS a
   * trailing "Other…" option that reveals a free-text input. Unlike
   * `options`, this is NOT a closed vocabulary — the field this backs
   * (Risk carryForwardOwner, OQ escalationOwner) deliberately also accepts
   * a named party absent from the list (a team, a role, an external
   * party — see #2544). Ignored when `options` is also set.
   */
  directoryOptions?: { value: string; label: string }[];
}

export interface DispositionOption {
  key: string;
  label: string;
  fields: DispositionField[];
}

interface DispositionCardProps {
  /** The agent's proposal, shown read-only above the option buttons.
   *  Omitted entirely (no proposal panel rendered) for a manual dispose
   *  with no agent proposal. */
  proposal?: {
    optionLabel: string;
    summary: string;
    basis?: string | null;
  } | null;
  options: DispositionOption[];
  /** Pre-selects an option and pre-fills its fields — e.g. the proposal's
   *  own suggested option/text, so Accept-as-proposed needs no typing and
   *  Modify-then-accept starts from the proposal's own wording. */
  defaultOptionKey?: string;
  defaultFieldValues?: Record<string, string>;
  submitting?: boolean;
  errorMessage?: string | null;
  onAccept: (optionKey: string, fieldValues: Record<string, string>) => void;
  onCancel?: () => void;
}

export default function DispositionCard({
  proposal,
  options,
  defaultOptionKey,
  defaultFieldValues,
  submitting = false,
  errorMessage,
  onAccept,
  onCancel,
}: DispositionCardProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(defaultOptionKey ?? null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(defaultFieldValues ?? {});
  // #3165 — per-field UI mode for `directoryOptions` fields: true shows the
  // free-text "Other" input, false shows the <select>. Initialized ONCE,
  // from whether the prefilled value (e.g. editing an existing disposition)
  // matches a directory entry AT MOUNT — a value that predates today's
  // directory (a former member, a renamed stakeholder) must still show as
  // editable text, not silently vanish into an unselected <select>.
  //
  // Deliberately NOT "start in Other mode whenever directoryOptions is
  // currently empty": the directory loads asynchronously (useOwnerDirectory
  // fetches after mount), and this initializer runs once, at mount, before
  // that fetch resolves. Treating a momentarily-empty list as "permanently
  // empty" would strand every row in free-text mode forever, even once the
  // directory finishes loading a moment later. An empty-right-now directory
  // instead just shows a two-item <select> ("Select…"/"Other…") — one extra
  // click to reach free text, not a trap.
  const [otherMode, setOtherMode] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const option of options) {
      for (const field of option.fields) {
        if (!field.directoryOptions || field.options) continue;
        const value = defaultFieldValues?.[field.key] ?? '';
        const matchesDirectory = value === '' || field.directoryOptions.some((o) => o.value === value);
        initial[field.key] = !matchesDirectory;
      }
    }
    return initial;
  });

  const selected = options.find((o) => o.key === selectedKey) ?? null;

  function selectOption(option: DispositionOption) {
    setSelectedKey(option.key);
    if (option.fields.length === 0) {
      onAccept(option.key, {});
    }
  }

  function submit() {
    if (!selected) return;
    onAccept(selected.key, fieldValues);
  }

  const canSubmit = selected !== null && selected.fields.every((f) => (fieldValues[f.key] ?? '').trim().length > 0);

  const btnPrimary: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
    background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer',
  };
  const btnGhost: React.CSSProperties = {
    ...btnPrimary, background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  };
  const btnOption = (active: boolean): React.CSSProperties => ({
    ...btnGhost,
    borderColor: active ? 'var(--primary)' : 'var(--border)',
    color: active ? 'var(--primary)' : 'var(--text-secondary)',
  });
  const inputStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 10px', width: '100%',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'var(--surface)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
      {proposal && (
        <div style={{ marginBottom: 10 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--primary)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            Agent proposal — {proposal.optionLabel}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', margin: 0 }}>{proposal.summary}</p>
          {proposal.basis && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 0', fontStyle: 'italic' }}>
              Basis: &ldquo;{proposal.basis}&rdquo;
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map((option) => (
          <button
            key={option.key}
            onClick={() => selectOption(option)}
            disabled={submitting}
            style={{ ...btnOption(selectedKey === option.key), opacity: submitting ? 0.6 : 1, padding: '4px 10px', fontSize: 11 }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {selected && selected.fields.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {selected.fields.map((field) => (
            <div key={field.key} style={{ marginBottom: 8 }}>
              <label style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                {field.label}
              </label>
              {field.options ? (
                <select
                  autoFocus
                  aria-label={field.label}
                  value={fieldValues[field.key] ?? ''}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="" disabled>Select…</option>
                  {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : field.directoryOptions ? (
                otherMode[field.key] ? (
                  <div>
                    <input
                      autoFocus
                      aria-label={field.label}
                      value={fieldValues[field.key] ?? ''}
                      onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      style={inputStyle}
                    />
                    {field.directoryOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setOtherMode((prev) => ({ ...prev, [field.key]: false }));
                          setFieldValues((prev) => ({ ...prev, [field.key]: '' }));
                        }}
                        style={{ ...btnGhost, marginTop: 6, padding: '2px 8px', fontSize: 11 }}
                      >
                        Choose from list instead
                      </button>
                    )}
                  </div>
                ) : (
                  <select
                    autoFocus
                    aria-label={field.label}
                    value={fieldValues[field.key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === DIRECTORY_OTHER_VALUE) {
                        setOtherMode((prev) => ({ ...prev, [field.key]: true }));
                        setFieldValues((prev) => ({ ...prev, [field.key]: '' }));
                      } else {
                        setFieldValues((prev) => ({ ...prev, [field.key]: v }));
                      }
                    }}
                    style={inputStyle}
                  >
                    <option value="" disabled>Select…</option>
                    {field.directoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    <option value={DIRECTORY_OTHER_VALUE}>Other…</option>
                  </select>
                )
              ) : field.short ? (
                <input
                  autoFocus
                  value={fieldValues[field.key] ?? ''}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  style={inputStyle}
                />
              ) : (
                <textarea
                  autoFocus
                  value={fieldValues[field.key] ?? ''}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                />
              )}
            </div>
          ))}
          {errorMessage && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error, #ef4444)', margin: '0 0 8px' }}>{errorMessage}</p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submit} disabled={!canSubmit || submitting} style={{ ...btnPrimary, opacity: (!canSubmit || submitting) ? 0.6 : 1 }}>
              {submitting ? 'Saving…' : 'Confirm'}
            </button>
            <button
              onClick={() => { setSelectedKey(null); setFieldValues({}); onCancel?.(); }}
              disabled={submitting}
              style={btnGhost}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
